// angular
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// components
import { SupplierProfileCardComponent } from '@app/core/components/supplier-profile-card/supplier-profile-card.component';
import { InstallerProfileCardComponent } from '@app/core/components/installer-profile-card/installer-profile-card.component';
import { DeveloperProfileCardComponent } from '@app/core/components/developer-profile-card/developer-profile-card.component';
import { ArchitectProfileCardComponent } from '@app/core/components/architect-profile-card/architect-profile-card.component';
import { NoContentComponent } from '@app/core/components/no-content/no-content.component';
import { PaginationComponent } from '@app/core/components/pagination/pagination.component';
// services
import { SolutionsService } from '@app/core/shared/services/solutions.service';
import { UserService } from '@app/shared/services/user.service';
// utils
import { normalizePeopleFilterType } from '@app/+solutions/shared/utils/normalize-people-profile.util';
import {
  canViewSolutionsProfileDetails,
  profileRealFirstName,
} from '@app/+solutions/shared/utils/solutions-profile-display.util';
// types
import {
  IPeopleProfile,
  PeopleFilterType,
} from '@app/core/shared/interfaces/solutions-profile.interface';

@Component({
  selector: 'pwj-profile-cards-list',
  standalone: true,
  imports: [
    RouterModule,
    NgxSkeletonLoaderModule,
    SupplierProfileCardComponent,
    InstallerProfileCardComponent,
    DeveloperProfileCardComponent,
    ArchitectProfileCardComponent,
    NoContentComponent,
    PaginationComponent,
  ],
  templateUrl: './profile-cards-list.component.html',
  styleUrl: './profile-cards-list.component.scss',
})
export class ProfileCardsListComponent implements OnInit {
  filterType = input<PeopleFilterType | null>(null);
  noContentMessageKey = input<string>('industries.noIndustries');

  list: IPeopleProfile[] = [];
  count = 0;
  isLoading = false;

  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _solutionsService = inject(SolutionsService);
  private readonly _userService = inject(UserService);

  readonly canViewDetails = computed(() =>
    canViewSolutionsProfileDetails(this._userService.userSignal())
  );

  constructor() {
    effect(() => {
      this.list = this._solutionsService.profilesList();
      this.count = this._solutionsService.profilesCount();
      this.isLoading = this._solutionsService.isProfilesLoading();
    });
  }

  ngOnInit(): void {
    this._activatedRoute.queryParams.subscribe(params => {
      const take = params['perPage'] ?? 12;
      const skip = params['page'] ? (Number(params['page']) - 1) * take : 0;
      const selectedParams: Params = { ...params, take, skip };
      const ft = this.filterType();
      if (ft != null) selectedParams['type'] = ft;
      this._solutionsService.getProfiles(selectedParams);
    });
  }

  /** Card layout per profile; uses filter when set, otherwise each item's API `type`. */
  getCardTypeForProfile(item: IPeopleProfile): PeopleFilterType {
    const fromFilter = this.filterType();
    if (fromFilter != null) return fromFilter;
    return this.resolvePeopleType(item);
  }

  /** Detail URL: /solutions/{segment}/{id} or /people/{id} when type is unknown. */
  getDetailRoute(profile: IPeopleProfile): string[] {
    const id = profile._id;
    if (!id) return ['/people'];
    const type = this.filterType() ?? this.resolvePeopleType(profile);
    const segment =
      type === 'SUPPLIER'
        ? 'suppliers'
        : type === 'INSTALLER'
          ? 'installers'
          : type === 'PROJECT_OWNER'
            ? 'developers'
            : type === 'ARCHITECT'
              ? 'architects'
              : null;
    return segment != null ? ['/solutions', segment, id] : ['/people', id];
  }

  getNavigationState(profile: IPeopleProfile): { previewFirstName?: string } {
    const previewFirstName = profileRealFirstName(profile);
    return previewFirstName ? { previewFirstName } : {};
  }

  private resolvePeopleType(item: IPeopleProfile): PeopleFilterType {
    return normalizePeopleFilterType(item.type as string) ?? 'SUPPLIER';
  }
}
